import type { ElectronAPI } from "../../types/electron";

export type VersionBackendCheckResult = {
  phase: "ok" | "error";
  message: string;
};

interface RunVersionBackendCheckOptions {
  electronApi?: Partial<ElectronAPI>;
  timeoutMs?: number;
}

export async function runVersionBackendCheck({
  electronApi,
  timeoutMs = 22000,
}: RunVersionBackendCheckOptions): Promise<VersionBackendCheckResult> {
  if (!electronApi?.backendEngine?.restart) {
    return { phase: "error", message: "Backend controls unavailable." };
  }

  try {
    return await withTimeout(runBackendCheck(electronApi), timeoutMs);
  } catch (error) {
    return {
      phase: "error",
      message: error instanceof Error ? error.message : "Backend check failed.",
    };
  }
}

async function runBackendCheck(
  electronApi: Partial<ElectronAPI>,
): Promise<VersionBackendCheckResult> {
  const backend = await electronApi.backendEngine?.restart?.();
  const runtime = await electronApi.desktopRuntime?.refresh?.();
  if (!backend?.ok) {
    return {
      phase: "error",
      message:
        backend?.detail || backend?.status?.detail || "Backend restart failed.",
    };
  }
  return {
    phase: "ok",
    message: runtime?.detail || backend.status?.detail || "Backend is live.",
  };
}

function withTimeout<T>(task: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(
        new Error(
          "Backend check timed out. Backend may still be starting; check /tmp/fintheon-backend.log.",
        ),
      );
    }, timeoutMs);
    task.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}
